/**
 * 방 관리 패널(관리자) — 목록·자동 새로고침·유휴 일괄 정리 — §9.2.
 *
 * 목록은 **주기적으로 다시 읽는다**: 방 상태는 서버에서 계속 변한다(호스트가
 * 끊기고, 참가자가 들어오고, 스위퍼가 거둬 간다). 손으로 눌러야만 갱신되는
 * 화면은 곧 틀린 화면이 되고, 그 틀린 화면을 보고 파기를 누르게 된다.
 *
 * 일괄 정리는 **무엇이 닫힐지 먼저 보여 준다**. 미리보기와 실행은 서버의
 * 같은 함수를 쓰므로 보여 준 것과 지운 것이 같다.
 *
 * ── 음성 녹음 배너(S10) ────────────────────────────────────────────────
 *
 * 녹음 중인 방이 하나라도 있으면 **표 위에 상시 배너**가 뜨고, 거기서
 * 한 번에 전부 끌 수 있다. 표의 방별 배지·버튼만으로는 부족한 이유:
 * 방이 20개면 녹음 중인 하나를 찾으려고 표를 훑어야 하고, S9 가 가르친
 * 것은 정확히 **찾아야 알 수 있는 사실은 아무도 모른다**는 것이다.
 * 켜기가 클릭 한 번이 된 이상, 켜져 있다는 사실도 클릭 없이 보여야 하고
 * 끄는 것도 클릭 한 번이어야 한다.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '@cupia/ui/button'
import { Card, CardContent } from '@cupia/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@cupia/ui/alert'
import { Spinner } from '@cupia/ui/spinner'
import { confirm } from '@cupia/ui/confirm-dialog'
import { Archive, Eraser, MicOff } from 'lucide-react'
import { RoomTable } from './room-table'
import {
  adminJoinRoom,
  backupRooms,
  cleanupIdleRooms,
  listAllRooms,
  previewIdleRooms,
  setRoomDump,
  type RoomSummary,
} from '../lib/rooms-admin'
import { ApiError } from '../lib/api'
import { saveCredential } from '../lib/session'

/** 목록 자동 갱신 주기. 방 상태는 계속 변한다. */
const POLL_MS = 5000

export interface RoomAdminPanelProps {
  onNotify?: (message: string) => void
}

export function RoomAdminPanel({ onNotify }: RoomAdminPanelProps): React.ReactElement {
  const navigate = useNavigate()
  const [rooms, setRooms] = useState<RoomSummary[] | null>(null)
  const [thresholdS, setThresholdS] = useState(1800)
  const [busy, setBusy] = useState(false)
  // 폴링이 조작 중에 끼어들어 표를 갈아엎지 않게 한다(누르려던 행이
  // 사라지면 엉뚱한 방을 파기하게 된다).
  const busyRef = useRef(false)
  busyRef.current = busy

  const notify = useCallback((m: string) => onNotify?.(m), [onNotify])

  const load = useCallback(async () => {
    try {
      const res = await listAllRooms()
      // 응답 모양을 **믿지 않는다**. 목록이 배열이 아니면(구 서버·프록시가
      // 끼어든 응답·부분 장애) 여기서 걸러야 한다 — 이 패널은 관리 화면
      // 안에 얹혀 있어서, 여기서 던지면 계정 관리까지 통째로 사라진다.
      setRooms(Array.isArray(res?.rooms) ? res.rooms : [])
      if (res?.idle_threshold_s) setThresholdS(res.idle_threshold_s)
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setRooms([])
        return
      }
      setRooms([])
      notify('방 목록을 불러오지 못했습니다.')
    }
  }, [notify])

  useEffect(() => {
    void load()
    const id = setInterval(() => {
      if (!busyRef.current) void load()
    }, POLL_MS)
    return () => clearInterval(id)
  }, [load])

  /** 유휴 일괄 정리 — 미리보기 → 백업 권유 → 실행. */
  const onCleanup = useCallback(async () => {
    setBusy(true)
    try {
      const preview = await previewIdleRooms(thresholdS)
      const targets = Array.isArray(preview?.rooms) ? preview.rooms : []
      if (targets.length === 0) {
        notify('정리할 유휴 회의실이 없습니다.')
        return
      }
      const withContent = targets.filter((r) => r.captions > 0 || r.chats > 0)
      const names = targets.map((r) => r.short).join(', ')
      const minutes = Math.floor((preview?.threshold_s ?? thresholdS) / 60)

      // 무엇이 닫히는지 **이름으로** 보여 준다. 개수만 말하면 무엇을 지우는지
      // 모른 채 누르게 된다.
      const ok = await confirm({
        title: `유휴 회의실 ${targets.length}개를 파기할까요?`,
        description:
          `${minutes}분 이상 활동이 없는 회의실입니다: ${names}. ` +
          (withContent.length > 0
            ? `이 중 ${withContent.length}개에 저장되지 않은 대화가 있습니다. 먼저 백업하는 것을 권합니다.`
            : '저장된 내용은 없습니다.') +
          ' 파기하면 되돌릴 수 없습니다.',
        confirmLabel: withContent.length > 0 ? '백업 후 파기' : '파기',
        cancelLabel: '취소',
        variant: withContent.length > 0 ? 'default' : 'destructive',
      })
      if (!ok) return

      if (withContent.length > 0) {
        const res = await backupRooms(
          withContent.map((r) => r.room),
          'txt',
          (done, total) => notify(`백업 중… ${done}/${total}`),
        )
        if (res.failed.length > 0) {
          // 하나라도 못 건졌으면 파기하지 않는다 — 백업을 고른 사람의
          // 의도는 "내용을 잃지 않는 것" 이다.
          notify(
            `백업 실패 ${res.failed.length}건 — 파기를 중단했습니다. 다시 시도해 주세요.`,
          )
          return
        }
        notify(`백업 완료 ${res.ok.length}건. 파기를 진행합니다.`)
      }

      const done = await cleanupIdleRooms(thresholdS)
      notify(`유휴 회의실 ${done.closed}개를 파기했습니다.`)
      await load()
    } catch (e) {
      notify(e instanceof ApiError ? e.message : '정리하지 못했습니다.')
    } finally {
      setBusy(false)
    }
  }, [thresholdS, notify, load])

  /** 내용이 있는 방 전체 백업 — 정리 전 안전망. */
  const onBackupAll = useCallback(async () => {
    const targets = (rooms ?? []).filter((r) => r.captions > 0 || r.chats > 0)
    if (targets.length === 0) {
      notify('백업할 대화가 있는 회의실이 없습니다.')
      return
    }
    setBusy(true)
    try {
      const res = await backupRooms(
        targets.map((r) => r.room),
        'txt',
        (done, total) => notify(`백업 중… ${done}/${total}`),
      )
      notify(
        res.failed.length === 0
          ? `회의록 ${res.ok.length}건을 저장했습니다.`
          : `저장 ${res.ok.length}건 · 실패 ${res.failed.length}건.`,
      )
    } finally {
      setBusy(false)
    }
  }, [rooms, notify])

  /**
   * 녹음 중인 방 **전부** 한 번에 끈다 — S10 의 "끄는 길이 켜는 길보다
   * 쉬워야 한다" 를 실행하는 손잡이.
   *
   * 확인을 받지 **않는다**. 이 화면의 다른 조작(파기·백업 후 파기)은 전부
   * 확인을 거치지만 여기는 반대 방향이다: 확인 대화는 되돌릴 수 없는
   * 조작을 늦추려고 있는 것이고, 녹음 중지는 되돌릴 수 있으며(다시 켜면
   * 된다) 늦추면 안 되는 조작이다. 실수로 껐다면 다시 켜면 되지만,
   * 실수로 켜 둔 채 잊으면 그게 S9 다.
   */
  const onStopAllDumps = useCallback(
    async (targets: RoomSummary[]) => {
      setBusy(true)
      try {
        let failed = 0
        for (const r of targets) {
          try {
            await setRoomDump(r.room, false)
          } catch {
            // 하나가 실패해도 나머지는 끈다 — 목적은 "최대한 멈추는 것" 이다.
            failed += 1
          }
        }
        notify(
          failed === 0
            ? `음성 녹음 ${targets.length}건을 모두 중지했습니다.`
            : `중지 ${targets.length - failed}건 · 실패 ${failed}건. 표에서 남은 방을 확인해 주세요.`,
        )
        await load()
      } finally {
        setBusy(false)
      }
    },
    [notify, load],
  )

  /**
   * 관리자가 남의 방에 뷰어로 참가한다(S22) — 서버가 감사 로그에 남긴다.
   *
   * 패스코드 없는 직접 참가라 자격도 그 모양을 따른다: `passcode` 는 두지
   * 않는다(session.ts `RoomCredential` — 관리자는 패스코드를 쓴 적이 없다).
   * 재교환 비밀이 없는 이 자격의 만료 전 갱신은 **stream 단독 경로**
   * `/api/rooms/{room}/refresh`(lib/token.ts refreshViaStream) 가 맡는다 —
   * 10분 뒤 새로고침·재접속이 끊기던 것이 codex Q1 회귀였다(TASK_sec_fix_e #1).
   */
  const onJoin = useCallback(
    async (r: RoomSummary) => {
      try {
        const res = await adminJoinRoom(r.room)
        saveCredential({
          room: res.room,
          key: res.view_key ?? '',
          role: 'viewer',
          token: res.token,
          tokenExp: Date.now() + (res.token_ttl_s ?? 600) * 1000,
          src: res.src,
          dst: res.dst,
          bidirectional: res.bidirectional,
        })
        void navigate(`/v/${encodeURIComponent(res.room)}`)
      } catch (e) {
        notify(e instanceof ApiError ? e.message : '참가하지 못했습니다.')
      }
    },
    [navigate, notify],
  )

  if (rooms === null) {
    return (
      <div className="flex items-center gap-2 p-4 text-[var(--color-muted-foreground)]">
        <Spinner className="h-4 w-4" />
        회의실 목록을 불러오는 중…
      </div>
    )
  }

  const orphaned = rooms.filter((r) => !r.host_connected && r.state !== 'closed')
  // 녹음 중인 방 — 배너의 근거. env 전역으로 켜진 방은 여기서 끌 수 없으므로
  // (운영자가 .env 를 지우고 재기동해야 한다) 갈라서 센다.
  const recording = rooms.filter((r) => r.dump?.on)
  const stoppable = recording.filter((r) => r.dump?.source === 'admin')
  const envWide = recording.length > stoppable.length

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">회의실 관리</span>
          <div className="ms-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => void onBackupAll()}
              data-testid="room-backup-all"
            >
              <Archive className="h-3.5 w-3.5" />
              전체 대화 백업
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => void onCleanup()}
              data-testid="room-cleanup"
            >
              <Eraser className="h-3.5 w-3.5" />
              유휴 방 정리
            </Button>
          </div>
        </div>

        {/* S10 — **녹음 중이면 가장 먼저, 가장 크게 말한다.** 이 배너가
            존재하는 이유는 S9 하나다: 조용히 켜져 있는 진단이 곧 실패
            모드다. 회의 음성이 디스크에 쌓이는 동안 이 화면이 아무 말도
            하지 않으면, 켠 사람이 잊는 순간 아무도 모른다. */}
        {recording.length > 0 && (
          <Alert variant="destructive" data-testid="room-recording-alert">
            <AlertTitle>음성 녹음 중인 회의실 {recording.length}개</AlertTitle>
            <AlertDescription className="flex flex-col gap-2">
              <span>
                이 회의들의 <strong>음성 원본</strong>이 서버 디스크에 저장되고
                있습니다. 자막·대화 기록과는 다른 축입니다. 진단이 끝났다면 지금
                꺼 주세요 — 저장된 파일은 3일 뒤 자동 정리되지만,{' '}
                <strong>녹음 자체는 끄기 전까지 계속됩니다.</strong>
                {envWide &&
                  ' 일부는 서버 설정(env)으로 켜져 있어 화면에서 끌 수 없습니다 — 운영자가 .env 를 정리하고 재기동해야 합니다(docs/DUMP_OPS.md).'}
              </span>
              {stoppable.length > 0 && (
                <Button
                  className="self-start"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => void onStopAllDumps(stoppable)}
                  data-testid="room-stop-all-dumps"
                >
                  <MicOff className="h-3.5 w-3.5" />
                  녹음 {stoppable.length}건 모두 중지
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* 호스트가 끊긴 채 살아 있는 방 — 사용자가 겪은 바로 그 상황이다.
            먼저 말해 주지 않으면 표를 훑어야 알 수 있다. */}
        {orphaned.length > 0 && (
          <Alert data-testid="room-orphan-alert">
            <AlertTitle>호스트가 접속하지 않은 회의실 {orphaned.length}개</AlertTitle>
            <AlertDescription>
              브라우저를 닫아도 회의실은 재접속을 위해 잠시 열려 있습니다. 끝난
              회의라면 파기해 주세요.
            </AlertDescription>
          </Alert>
        )}

        <RoomTable
          rooms={rooms}
          onRefresh={() => void load()}
          onNotify={onNotify}
          canBackup
          canRecord
          onJoin={(r) => void onJoin(r)}
          autoRefreshing={!busy}
        />
      </CardContent>
    </Card>
  )
}
